import { IconArrowLeft, IconChart8, IconClose, IconPackage2, IconPin, IconUsb, IconWarning, ItemData, List, MobileSheet, Pill } from "@geotab/zenith";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslationContext } from "../../../../../ui/framework/hooks/TranslationContext";
import { useStoreContext } from "../../../../../ui/framework/hooks/StoreContext";
import { observeStore } from "../../../../../utils/store";
import { vehicleStatus, networkStatus, gpsStatus, malfunctionsStatus, ConnectionDialogListData, DisplayDetailEnum, isNoVehicle, getFilteredLogs } from "../helpers/ConnectionHelper";
import { useRouterContext } from "../../../../../ui/framework/hooks/RouterContext";
import { populateAndGroupLogs } from "../../../../hos/eldMalfunctions/helpers/malfunctionDataProcessor";
import { useHosUtilsContext } from "../../../../../ui/framework/hooks/HosUtilsContext";
import { useDutyStatusLogServiceContext } from "../../../../../ui/framework/hooks/DutyStatusLogServiceContext";
import { getPageState, ItemListData } from "../helpers/Helper";
import { ConnectionDialogTranslationsDictionary } from "../helpers/ConnectionDialogTranslation";
import { NetworkStates } from "../../../../../store/mobile/types";

interface ConnectionStatusDialogProps {
    connectionMobileSheet: boolean;
    setConnectionMobileSheet: React.Dispatch<React.SetStateAction<boolean>>;
    displayDetail: DisplayDetailEnum;
    setDisplayDetail: React.Dispatch<React.SetStateAction<DisplayDetailEnum>>;
}

const ConnectionStatusDialog: FC<ConnectionStatusDialogProps> =
    ({connectionMobileSheet, setConnectionMobileSheet, displayDetail, setDisplayDetail }) => {

    const { translate } = useTranslationContext();
    const { store } = useStoreContext();
    const { hosUtils } = useHosUtilsContext();
    const { dutyStatusLogService } = useDutyStatusLogServiceContext();
    const { router } = useRouterContext();

    const ref = useRef(null);

    const [title, setTitle] = useState(translate(ConnectionDialogTranslationsDictionary.connectionDialogTitle));
    const [listData, setListData] = useState<ConnectionDialogListData>({
        vehicleStatus: {},
        networkStatus: {},
        gpsStatus: {},
        malfunctionsStatus: {}
    });

    const navigateToMalfunctionsPage = () => {
        if (!getPageState(store).isLocked) {
            router.navigate("hos/malfunctions", true);
            setConnectionMobileSheet(false);
        }
    };

    const backPress = () => {
        setDisplayDetail(DisplayDetailEnum.default);
        setTitle(translate(ConnectionDialogTranslationsDictionary.connectionDialogTitle));
    };

    const doNetworkMessage = () => {
        const online = store.getState().mobile.online;
        setListData(prevListData => {
            if (online === NetworkStates.ONLINE) {
                return {
                    ...prevListData,
                    networkStatus: {
                        ...prevListData.networkStatus,
                        displayError: false,
                        primary: translate(networkStatus(false))
                    }
                };
            }

            // openBanner(DisplayDetailEnum.network);
            return {
                ...prevListData,
                networkStatus: {
                    ...prevListData.networkStatus,
                    displayError: true,
                    primary: translate(networkStatus(true))
                }
            };
        });
    };

    const doMalfunction = () => {
        if (!store.getState().mobile.eldMalfunction && !store.getState().mobile.eldDiagnostic) {
            return;
        }

        setListData(prevListData => {
            let filterStartDateMs = hosUtils.getUnidentifiedDrivingRetentionStartDate(store.getState().data.ELDJurisdiction === "CANELD").getTime();
            let { resultCollection } = populateAndGroupLogs(getFilteredLogs(dutyStatusLogService), filterStartDateMs);
            let getMalfunctionCount = resultCollection.size;
            let malfunctionStatus = prevListData.malfunctionsStatus;

            if (getMalfunctionCount === 0) {
                return {
                    ...prevListData,
                    malfunctionsStatus: { ...malfunctionStatus, displayError: false, primary: translate(malfunctionsStatus(false)), status: undefined }
                };
            }


            // openBanner(DisplayDetailEnum.malfunctions);
            return {
                ...prevListData,
                malfunctionsStatus: { ...malfunctionStatus, displayError: true, primary: translate(malfunctionsStatus(true)), status: getMalfunctionCount }
            };
        });
    };

    const doVehicleStatus = () => {
        setListData(prevListData => {
            if (isNoVehicle(store)) {
                return {
                    ...prevListData,
                    vehicleStatus: { ...prevListData.vehicleStatus, icon: IconClose, displayError: false, primary: translate(ConnectionDialogTranslationsDictionary.noVehicle) }
                } ;
            }

            if (!store.getState().mobile.deviceCommunicating && !store.getState().mobile.ioxAttached) {
                return {
                    ...prevListData,
                    vehicleStatus: { ...prevListData.vehicleStatus, icon: IconPackage2, displayError: true, primary: translate(vehicleStatus(true, store)) }
                };
            }
            return {
                ...prevListData,
                vehicleStatus: { ...prevListData.vehicleStatus, icon: IconPackage2, displayError: false, primary: translate(vehicleStatus(false, store)) }
            };
        });
    };

    const doGpsStatus = () => {
        setListData(prevListData => {
            if (!store.getState().mobile.gpsConnected && !store.getState().mobile.ioxAttached) {
                return {
                    ...prevListData,
                    gpsStatus: { ...prevListData.gpsStatus, displayError: true, primary: translate(gpsStatus(true)) }
                };
            }

            return {
                ...prevListData,
                gpsStatus: { ...prevListData.gpsStatus, displayError: false, primary: translate(gpsStatus(false)) }
            };
        });
    };

    useEffect(() => {
        // load initial state
        const data: ConnectionDialogListData = {
            vehicleStatus: { id: "vehicleStatus", primary: translate(vehicleStatus(false, store)), icon: isNoVehicle(store) ? IconClose : IconPackage2, onClick: undefined, displayError: false, status: undefined },
            networkStatus: { id: "networkStatus", primary: translate(networkStatus(false)), icon: IconChart8, onClick: () => { setDisplayDetail(DisplayDetailEnum.network); }, displayError: false, status: undefined },
            gpsStatus: { id: "gpsStatus", primary: translate(gpsStatus(true)), icon: IconPin, onClick: undefined, displayError: true, status: undefined },
            malfunctionsStatus: { id: "malfunctionsStatus", primary: translate(malfunctionsStatus(false)), icon: IconUsb, onClick: () => { navigateToMalfunctionsPage(); }, displayError: false, status: undefined }
        };

        setListData(data);

        // setup observers for store.mobile changes
        let observers: (() => void)[] = [];
        observers.push(observeStore(store, state => state.mobile.online, doNetworkMessage));
        observers.push(observeStore(store, state => [state.mobile.eldMalfunction, state.mobile.eldDiagnostic], doMalfunction));
        observers.push(observeStore(store, state => [state.mobile.deviceCommunicating, state.mobile.ioxAttached], doVehicleStatus));
        observers.push(observeStore(store, state => [state.mobile.gpsConnected || state.mobile.ioxAttached], doGpsStatus));

        return () => observers.forEach(unsubscribe => unsubscribe());
    }, []);

    useEffect(() => {
        if (connectionMobileSheet) {
            return;
        }

        // reset title and display to default when closing the sheet
        setDisplayDetail(DisplayDetailEnum.default);
        setTitle(translate(ConnectionDialogTranslationsDictionary.connectionDialogTitle));
    }, [connectionMobileSheet]);

    useEffect(() => {
        if (!isNoVehicle(store)) {
            return;
        }

        setListData(prevListData => {
            if (isNoVehicle(store)) {
                return {
                    ...prevListData,
                    vehicleStatus: { ...prevListData.vehicleStatus, icon: IconClose, displayError: false, primary: translate(vehicleStatus(true, store)) }
                };
            }

            return prevListData;
        });

    }, [isNoVehicle(store)]);

    const displayStatus = (item) => {
        if (item.displayError) {
            return (
                <Pill type="error">
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <IconWarning size="large" />
                        {item.status}
                    </span>
                </Pill>
            );
        }
        return undefined;
    };

    const networkMoreInfo = useMemo(() => {
        setTitle(translate(ConnectionDialogTranslationsDictionary.networkDisconnectedDetail.title));
        return (
            <div style={{ padding: "8px", color: "var(--text-primary)" }}>
                <span>
                    {translate(ConnectionDialogTranslationsDictionary.networkDisconnectedDetail.paragraphOne)}
                    <ul>
                        <li>{translate(ConnectionDialogTranslationsDictionary.networkDisconnectedDetail.bulletPointOne)}</li>
                        <li>{translate(ConnectionDialogTranslationsDictionary.networkDisconnectedDetail.bulletPointTwo)}</li>
                        <li>{translate(ConnectionDialogTranslationsDictionary.networkDisconnectedDetail.bulletPointThree)}</li>
                    </ul>
                    {translate(ConnectionDialogTranslationsDictionary.contactYourAdmin)}
                </span>
            </div>
        );
    }, [displayDetail]);

    const gpsMoreInfo = useMemo(() => {
        setTitle(translate(ConnectionDialogTranslationsDictionary.gpsDisconnectedDetail.title));

        return (
            <div style={{ padding: "8px", color: "var(--text-primary)" }}>
                <span>
                    {translate(ConnectionDialogTranslationsDictionary.gpsDisconnectedDetail.paragraphOne)}
                    <ul>
                        <li>{translate(ConnectionDialogTranslationsDictionary.gpsDisconnectedDetail.bulletPointOne)}</li>
                        <li>{translate(ConnectionDialogTranslationsDictionary.gpsDisconnectedDetail.bulletPointTwo)}</li>
                    </ul>
                    {translate(ConnectionDialogTranslationsDictionary.contactYourAdmin)}
                </span>
            </div>
        );
    }, [displayDetail]);

    const defaultDisplay = () => {
        return (
            <List>
                {Object.keys(listData).map((key, index) => {
                    const item: ItemListData = listData[key];
                    return (
                        <ItemData key={index} icon={item.icon} id={item.id} name={item.primary} onClick={item.displayError ? item.onClick : undefined} status={displayStatus(item)}  />
                    );
                })}
            </List>
        );
    };

    const getDisplay = useMemo(() => {
        switch (displayDetail) {
            case DisplayDetailEnum.vehicleDisconnect:
            case DisplayDetailEnum.network:
                return networkMoreInfo;
            case DisplayDetailEnum.gps:
                return gpsMoreInfo;
            default:
                return defaultDisplay();
        }
    }, [displayDetail, listData]);

    const renderBackIcon = useMemo(() => {
        if (displayDetail === DisplayDetailEnum.default) {
            return undefined;
        }
        return (
            <div onClick={backPress} style={{ cursor: "pointer" }}>
                <IconArrowLeft size="large" />
            </div>
        );
    }, [displayDetail]);

    return (
        <>
            <MobileSheet
                label={translate(title)}
                triggerRef={ref}
                isOpen={connectionMobileSheet}
                onHidePanel={() => setConnectionMobileSheet(false)}
                onCloseClick={() => setConnectionMobileSheet(false)}>
                <MobileSheet.Title>
                    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {renderBackIcon}
                        {translate(title)}
                    </span>
                </MobileSheet.Title>
                <MobileSheet.Content>
                    { getDisplay }
                </MobileSheet.Content>
            </MobileSheet>
        </>
    );
};

export default ConnectionStatusDialog;